import { useMutation } from '@tanstack/react-query'
import React, { useEffect, useRef, useState } from 'react'
import { useMount } from 'react-use'

import { useDeleteServiceMutation } from '@/components/flows/CompleteRegistrationFlow/steps/1_CompanyServices/hooks/useDeleteServiceMutation'
import ActionButton from '@/components/misc/ActionButton'
import SanitizedHtml from '@/components/misc/SanitizedHtml'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { API_PATH_INTERNAL } from '@/lib/constants'
import { buildDebugTooltip } from '@/lib/helpers/helperFunctions'
import serviceService from '@/lib/services/serviceService'

interface Props {
  companyId: number
  serviceId?: number
  serviceAreaId: number
  serviceAreaName: string
  onChangeSaved: () => void
  searchTerm: string
}

const CompanyServiceToggleButton: React.FC<Props> = ({
  companyId,
  serviceId,
  serviceAreaId,
  serviceAreaName,
  onChangeSaved,
  searchTerm,
}) => {
  const [isSelected, setIsSelected] = useState(serviceId !== undefined)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const textRef = useRef<HTMLSpanElement>(null)

  // Watch for changes
  useEffect(() => {
    setIsSelected(serviceId !== undefined)
  }, [serviceId])

  const createServiceMutation = useMutation({
    mutationFn: async () => {
      setIsSelected(true)

      return serviceService.createService({
        company: `${API_PATH_INTERNAL}/companies/${companyId}`,
        serviceArea: `${API_PATH_INTERNAL}/service-areas/${serviceAreaId}`,
      })
    },
    onSuccess: () => {
      onChangeSaved()
    },
    onError: () => {
      setIsSelected(false)
    },
  })

  const deleteServiceMutation = useDeleteServiceMutation({
    onMutate: () => setIsSelected(false),
    onSuccess: onChangeSaved,
    onError: () => setIsSelected(true),
  })

  // I.e. the prop passed doesn't reflect what we now think the state should be
  // which will be because we're waiting to re-query in the parent component
  const isWaitingForSync = (serviceId !== undefined) !== isSelected

  function tokeniseAndFormatServiceAreaName() {
    let result = serviceAreaName

    const searchWords = searchTerm.split(' ')

    const escapedSearchWords = searchWords.map((searchWord) =>
      searchWord.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'),
    )

    const regEx = new RegExp(`(${escapedSearchWords.join('|')})`, 'ig')

    result = result.replaceAll(
      regEx,
      '<strong style="color: inherit;">$1</strong>',
    )

    return result
  }

  useMount(() => {
    const textRefElement = textRef.current

    if (!textRefElement) {
      return
    }

    setIsOverflowing(textRefElement.scrollWidth > textRefElement.clientWidth)
  })

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <ActionButton
          variant={isSelected ? 'primary' : 'secondary'}
          title={buildDebugTooltip({ serviceAreaId, serviceId })}
          size="lg"
          className="grow rounded-4xl md:w-53.75 lg:grow-0"
          isProcessing={
            createServiceMutation.isPending ||
            deleteServiceMutation.isPending ||
            isWaitingForSync
          }
          showIsProcessingText={false}
          onClick={() => {
            if (isSelected && serviceId !== undefined) {
              deleteServiceMutation.mutate(serviceId)
            } else {
              createServiceMutation.mutate()
            }
          }}
        >
          <SanitizedHtml
            html={tokeniseAndFormatServiceAreaName()}
            ref={textRef}
            as="span"
            className="overflow-x-hidden rounded-4xl text-[13px] text-ellipsis
              whitespace-nowrap"
          />
        </ActionButton>
      </TooltipTrigger>

      {isOverflowing && <TooltipContent>{serviceAreaName}</TooltipContent>}
    </Tooltip>
  )
}

export default CompanyServiceToggleButton
